import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { UI2DateTimeFieldProps } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import { UI2_DATE_TIME_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import {
  formatDateTimeValue,
  getDefaultDateTimePlaceholder,
  isDateTimeValueEmpty,
} from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'

import { DateTimeInput } from 'browser/components/atomic-elements/atoms/input/date-time-input/date-time-input'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import {
  IUI2PulseHighlightRef,
  UI2PulseHighlight,
} from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'
import './_ui2-date-time-field.scss'

import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'

interface IUI2DateTimeWebExtras {
  onChange?: (value: any) => void
  errors?: any[]
  className?: string
  daySize?: number
  isLoading?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2DateTimeProps extends UI2DateTimeFieldProps, IUI2DateTimeWebExtras {}

export class UI2DateTime extends React.Component<IUI2DateTimeProps> {
  public static contextType = FormFocusTargetContext
  declare context: IFormFocusTargetContext<HTMLElement>

  public static defaultProps: Partial<IUI2DateTimeProps> = {
    ...UI2_DATE_TIME_FIELD_DEFAULT_PROPS,
    minuteStep: 5, // web-specific: native date picker minute increment
  }

  private pulseRef = React.createRef<IUI2PulseHighlightRef>()

  public render() {
    const { className } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-date-time-field', className)}
        isValueEmpty={isDateTimeValueEmpty}
        renderReadOnly={this.renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
        pulseRef={this.pulseRef}
      >
        <UI2PulseHighlight ref={this.pulseRef}>
          <UI2FieldControl
            className="ui2-date-time-field__control"
            leftContent={this.renderModeIcon()}
          >
            {this.renderDateTime()}
          </UI2FieldControl>
        </UI2PulseHighlight>
      </UI2FieldShell>
    )
  }

  /**
   * On MW the picker is a full-width sheet over the form, so on commit the
   * field itself can be off-screen — `focusPath` scrolls it back and pulses
   * it. Same tail on DW, where the popover is anchored and the scroll is
   * usually a no-op but the pulse still confirms which field took the value.
   * The bare pulse is the fallback outside a focus-target provider.
   */
  private returnFocusToField = (): void => {
    const valuePath: string[] | undefined = (this.props as any).frames?.getContext('valuePath')
    const path = valuePath?.join('.')
    if (path && this.context?.focusPath) {
      void this.context.focusPath(path)
    } else {
      this.pulseRef.current?.pulse()
    }
  }

  private handleChange = (value: any): void => {
    this.props.onChange?.(value)
    // Only on a commit. The inline clear also routes through here, and the
    // user is already looking at the field they just cleared.
    if (value != null) this.returnFocusToField()
  }

  /** Decoration saying what this field picks — same leading slot as ui2:scanField. */
  private renderModeIcon(): React.ReactNode {
    const { mode = 'datetime' } = this.props
    return (
      <UI2Icon
        name={mode === 'time' ? UI2IconNames.CLOCK : UI2IconNames.CALENDAR}
        size={14}
        className='ui2-date-time-field__modeIcon'
      />
    )
  }

  private renderDateTime(): React.ReactNode {
    const {
      value,
      mode = 'datetime',
      valueMode,
      showTimezoneInput,
      minDate,
      maxDate,
      daySize,
      placeholder,
      minuteStep,
      isDisabled,
      isClearable,
      label,
    } = this.props
    return (
      <DateTimeInput
        value={value}
        onChange={this.handleChange}
        mode={mode}
        valueMode={valueMode}
        showTimezoneInput={showTimezoneInput}
        isDisabled={isDisabled}
        isClearable={isClearable}
        placeholder={placeholder ?? getDefaultDateTimePlaceholder(mode, label)}
        minDate={minDate}
        maxDate={maxDate}
        daySize={daySize}
        minuteStep={minuteStep}
      />
    )
  }

  /**
   * The shell renders the empty state itself before ever calling this, so
   * there is no empty branch here. A value that won't parse falls back to its
   * raw string rather than reading as absent.
   */
  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const { mode = 'datetime', showTimezoneInput } = this.props
    const formatted = formatDateTimeValue(value, mode, showTimezoneInput)
    const display = formatted || String(_.get(value, 'dateTime', value) ?? '')
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <div className='ui2-date-time-field--readOnly'>{display}</div>
        </div>
        {editNode}
      </>
    )
  }
}
